import { Assessment, WorkManifest, WorkOutcome } from "@vericus/cadmus-common";

import { __GLOBAL_TENANT } from "@/client/globals";
import * as config from "@/config";

export interface FeedbackData {
  work_outcome: WorkOutcome;
  manifest: WorkManifest;
  assessment: Assessment;
  feedback_visibility: FeedbackVisbility;
}

export interface FeedbackVisbility {
  feedback_view_level: "mark" | "feedback" | "answer";
  with_feedback_pdf: boolean;
}

/**
 * GET feedback for the latest final submission in a Work.
 *
 * @param workId - The work Id for the feedback.
 * @returns Promise that on resolve to `FeedbackData` object.
 */
export async function getFeedback(
  workId: string,
  tenant: string
): Promise<{ data?: FeedbackData; error?: Error; status: number }> {
  const headers = new Headers({
    "x-cadmus-role": "student",
    "x-cadmus-tenant": tenant,
  });

  const request: RequestInit = {
    method: "GET",
    headers,
    credentials: "include",
  };

  const response = await fetch(
    `${config.API_ENDPOINT}/api/feedback/${workId}`,
    request
  );
  if (!response.ok) {
    const error = new Error(
      `${response.status} message: ${response.statusText}`
    );
    return { data: undefined, error, status: response.status };
  }

  const json = await response.json();
  return { data: json, error: undefined, status: response.status };
}

/**
 * GET Feedback PDF blob for a given workId.
 */
export async function getFeedbackPdf(
  workId: string,
  tenant: string
): Promise<Blob> {
  const request: RequestInit = {
    method: "GET",
    headers: {
      "x-cadmus-role": "student",
      "x-cadmus-tenant": tenant,
    },
    credentials: "include",
  };
  const response = await fetch(
    `${config.API_ENDPOINT}/api/feedback/${workId}/download`,
    request
  );

  if (response.ok) {
    const data = await response.json();
    if (data.url) {
      const downloadRes = await fetch(data.url);
      const blob = await downloadRes.blob();
      return blob;
    }
  }

  throw new Error("Error downloading feedback");
}
